import { Button, createStyles, IconButton, makeStyles, Theme, Tooltip, Typography } from "@material-ui/core";
import * as React from "react";
import * as ThemeConstants from "../../Styles/Constants";
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../Actions/ActionFunctions";
import { GamePlayer, SnackbarStatus, SocketMessageType } from "../../Definitions/Definitions";
import { useWindowDimensions } from "../UI/UseWindowDimensions";
import { HoverHelp } from "../UI/HoverHelp";
import { useHistory, useParams } from "react-router";
import { useGameContext } from "../GameContext/GameContext";
import { OriginalGameState } from "../GameContext/OriginalGameState";

interface Props {
    maxPlayers: number;
    renderParameters: () => JSX.Element;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "1rem",
            marginTop: "3rem"
        },
        textFlex: {
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            gap: "0.5rem"
        },
        content: {
            display: "flex",
            flexDirection: "column",
            minWidth: 200,
            gap: "1rem"
        },
        outer: {
            display: "flex",
            justifyContent: "center",
            gap: "2rem"
        },
        kickButton: {
            padding: 0,
            minWidth: 50,
            maxWidth: 50,
            maxHeight: 22
        },
        joinButton: {
            padding: 0,
            minWidth: 50,
            maxWidth: 50
        }
    })
);

export const LobbyPage = (props: Props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const gameContext = useGameContext();
    const { width, height } = useWindowDimensions();

    const { roomIdString } = useParams<{ roomIdString: string; }>();
    const roomId = Number(roomIdString);

    const { actualLocalPlayer, playerList, hostRoomId, switchingSides } = gameContext.gameState;

    const copyShareLink = () => {
        const link = `https://${location.host}/join/${roomId == 0 ? hostRoomId : roomId}`;
        navigator.clipboard.writeText(link);
        dispatch(openSnackbar(SnackbarStatus.Success, "Share link copied to clipboard", 2000))
    }

    const kickPlayer = (playerId: string) => {
        gameContext.sendMessage(SocketMessageType.KickPlayer, playerId);
    }

    const switchSides = (spectate: boolean) => {
        gameContext.updateGameState((prevState: OriginalGameState) => {
            gameContext.sendMessage(spectate ? SocketMessageType.JoinSpectators : SocketMessageType.JoinPlayers, "");
            return {
                ...prevState,
                switchingSides: true
            }
        });
    }

    const getPlayerCount = () => {
        return playerList.filter(e => !e.isSpectator).length;
    }

    const renderPlayers = (spectators: boolean) => {
        return (
            <div style={{ minWidth: 250 }}>
                <div className={classes.textFlex}>
                    <Typography color="textSecondary" variant="h4">{spectators ? "Spectators" : "Players"}</Typography>
                    <Button
                        variant="outlined"
                        className={classes.joinButton}
                        color="primary"
                        disabled={(actualLocalPlayer?.isSpectator && spectators) || (!actualLocalPlayer?.isSpectator && !spectators) || switchingSides}
                        onClick={() => switchSides(spectators)}
                    >
                        Join
                    </Button>
                </div>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {
                        playerList.map((elem, i) => {
                            if (elem.isSpectator && !spectators) return;
                            if (!elem.isSpectator && spectators) return;

                            return (
                                <div key={i} className={classes.textFlex}>
                                    {(actualLocalPlayer?.isHost && playerList.length > 1) &&
                                        <Button
                                            variant="outlined"
                                            style={{ visibility: actualLocalPlayer.id == elem.id ? "hidden" : "visible" }}
                                            className={classes.kickButton}
                                            color="secondary"
                                            onClick={() => kickPlayer(elem.id)}
                                        >
                                            Kick
                                        </Button>
                                    }
                                    <Typography variant="body1">{elem.name}</Typography>
                                    {(elem.id == actualLocalPlayer?.id) &&
                                        <Typography variant="body1" style={{ color: ThemeConstants.PALETTE_ORANGE }}>(YOU)</Typography>
                                    }
                                    {elem.isHost &&
                                        <Typography variant="body1" style={{ color: ThemeConstants.PALETTE_GREEN }}>[HOST]</Typography>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.textFlex}>
                <Typography variant="h4">Room Code:</Typography>
                <Typography variant="h4"><b>{roomId == 0 ? (hostRoomId == 0 ? "Loading..." : hostRoomId) : roomId}</b></Typography>
                {(roomId != 0 || hostRoomId != 0) &&
                    <Tooltip title={<Typography variant="body1">Copy join link</Typography>}>
                        <IconButton style={{ padding: "0.25rem" }} onClick={copyShareLink}>
                            <FileCopyIcon />
                        </IconButton>
                    </Tooltip>
                }
            </div>
            <Typography color="textSecondary" variant="h5" style={{ marginTop: "-0.75rem" }}>{`${playerList.length}/${props.maxPlayers} Room Capacity`}</Typography>
            <div className={classes.outer} style={{ flexDirection: width < 800 ? "column" : "row" }}>
                <div className={classes.content}>
                    <div className={classes.textFlex}>
                        <Typography variant="body1">Game type: <b>Original</b></Typography>
                        {/*<HoverHelp text={"The original Word Arena game"} />*/}
                    </div>
                    {props.renderParameters()}
                    {(actualLocalPlayer?.isHost) &&
                        <>
                        <Button onClick={gameContext.startGame} disabled={getPlayerCount() == 0} style={{ marginTop: "1rem" }} color="primary" variant="outlined">
                            Start Game
                        </Button>
                        {getPlayerCount() == 0 &&
                            <Typography variant="body1" style={{ marginTop: "-0.5rem", textAlign: "center" }} color="error">Cannot start with 0 players</Typography>
                        }
                        </>
                    }
                </div>
                {renderPlayers(false)}
                {renderPlayers(true)}
            </div>
        </div>
    );
}